import React from "react";
import { Box, Flex, Grid, Spinner, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickRequestFileDownloadButton from "@/features/pathpick/components/request/PathPickRequestFileDownloadButton";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  formatRequestTime,
  getPathPickRequestStatusMeta,
  isPathPickRequestPending,
} from "@/features/pathpick/utils/request";
import { PathPickRequestItem } from "@/features/pathpick/types";

interface Props {
  item: PathPickRequestItem;
  // row — 칼럼 표의 한 행(데스크톱), card — 칼럼 없이 세로로 쌓는 카드(모바일, 가로 스크롤 방지)
  layout: "row" | "card";
  templateColumns: string;
  // 표 안에서 행을 잇는 구분선 — 그룹의 마지막 행에는 그리지 않는다.
  hasBorderBottom: boolean;
  onViewSyncedProducts: (item: PathPickRequestItem) => void;
}

interface CountLineProps {
  label: string;
  value: string;
  valueColor?: string;
}

const CountLine = ({
  label,
  value,
  valueColor = "#1D1D1B",
}: CountLineProps) => {
  return (
    <Flex gap={"6px"} alignItems={"center"}>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      <Text textStyle={"body-sm-bold"} color={valueColor}>
        {value}
      </Text>
    </Flex>
  );
};

function PathPickSyncDownloadHistoryListItem({
  item,
  layout,
  templateColumns,
  hasBorderBottom,
  onViewSyncedProducts,
}: Props) {
  const { t } = useI18nHelper();

  const isApiSync = item.requestType === "SYNC";

  const countValue = (count: number) =>
    t("path_pick:sync_download_history.count_value", { count });

  const kindLabel = t(
    isApiSync
      ? "path_pick:sync_download_history.kind_api_sync"
      : item.format === "NAVER"
      ? "path_pick:sync_download_history.channel_smart_store"
      : "path_pick:sync_download_history.channel_kream"
  );

  const statusMeta = getPathPickRequestStatusMeta(item.status, isApiSync);

  // 표와 카드가 같은 값을 그리도록 칸 내용은 한 번만 만들고 배치만 가른다.
  const kindCell = (
    <Flex flexDirection={"column"} gap={"2px"}>
      <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
        {kindLabel}
      </Text>
      {/* API 연동은 어느 채널로 연동했는지 format 원문(NAVER·MUSTIT·TRENBE·CAFE24)을 덧붙인다. */}
      {isApiSync && item.format && (
        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
          ({item.format})
        </Text>
      )}
    </Flex>
  );

  // 날짜는 그룹 헤더가 보여주므로 행에는 시각만 남긴다.
  const timeText = (
    <Text textStyle={"body-sm-medium"} color={"#444440"} flexShrink={0}>
      {formatRequestTime(item.requestedAt)}
    </Text>
  );

  const countLines = (
    <>
      <CountLine
        label={t("path_pick:sync_download_history.requested_count")}
        value={countValue(item.requestedItemCount)}
      />
      {/* API 연동은 처리 대상에서 빠진 "제외", 엑셀은 파일 생성 "실패"로 부른다. */}
      <CountLine
        label={t(
          isApiSync
            ? "path_pick:sync_download_history.excluded_count"
            : "path_pick:sync_download_history.failed_count"
        )}
        value={countValue(item.failureCount)}
        valueColor={item.failureCount > 0 ? "#EE7D7A" : "#1D1D1B"}
      />
      {/* API 연동 완료 수는 '지금도 연동 중'인 상품 수다 — successCount 는 처리 당시 값이라 연동을
          해제해도 그대로라서, 서버가 계산한 activeSyncCount 를 쓰고 해제분은 따로 보여 준다. */}
      <CountLine
        label={t(
          isApiSync
            ? "path_pick:sync_download_history.synced_count"
            : "path_pick:sync_download_history.file_ready_count"
        )}
        value={countValue(
          isApiSync
            ? item.activeSyncCount ?? item.successCount
            : item.successCount
        )}
        valueColor={"#16B41C"}
      />
      {isApiSync && (item.unsyncedCount ?? 0) > 0 && (
        <CountLine
          label={t("path_pick:sync_download_history.unsynced_count")}
          value={countValue(item.unsyncedCount ?? 0)}
        />
      )}
    </>
  );

  const statusBadge = (
    <Flex gap={"6px"} alignItems={"center"}>
      <PathPickBadge
        label={t(`path_pick:sync_download_history.${statusMeta.labelKey}`)}
        colorVariant={statusMeta.colorVariant}
        leadingIcon={statusMeta.leadingIcon}
        size={"medium"}
      />
      {/* 처리중(PENDING/PROCESSING)일 때만 스피너 노출 — 폴링이 살아있다는 신호 */}
      {isPathPickRequestPending(item.status) && (
        <Spinner
          size={"xs"}
          color={"#8F8F8C"}
          speed={"0.7s"}
          thickness={"2px"}
        />
      )}
    </Flex>
  );

  // 연동/다운로드 구분 없이 요청에 포함된 상품 내역을 볼 수 있다.
  const viewProductsButton = (width?: string) => (
    <AppButton
      variant={"white"}
      width={width}
      onClick={() => onViewSyncedProducts(item)}
    >
      <Text textStyle={"body-sm-bold"}>
        {t("path_pick:sync_download_history.view_request_products")}
      </Text>
    </AppButton>
  );

  // API 연동 행은 다운로드 파일이 없으므로 버튼 미노출
  const downloadButton = (width?: string) =>
    !isApiSync && (
      <PathPickRequestFileDownloadButton
        fileUrl={item.fileUrl}
        format={item.format}
        width={width}
      />
    );

  if (layout === "card") {
    return (
      <Flex
        flexDirection={"column"}
        gap={"10px"}
        padding={"12px 16px"}
        borderBottom={hasBorderBottom ? "0.5px solid #D4D4D1" : undefined}
      >
        <Flex
          justifyContent={"space-between"}
          alignItems={"flex-start"}
          gap={"8px"}
        >
          {kindCell}
          {timeText}
        </Flex>
        {statusBadge}
        <Flex columnGap={"12px"} rowGap={"2px"} flexWrap={"wrap"}>
          {countLines}
        </Flex>
        {/* AppButton은 Tooltip으로 감싸여 있어 폭 분배는 바깥 Box가 맡는다.
            다운로드 문구("스마트스토어 일괄등록 리스트 다운로드")가 길어 반반으로 나누면 잘리므로,
            다운로드는 글자 폭을 지키고 신청 상품 내역이 남는 폭만 쓴다. */}
        <Flex gap={"8px"}>
          <Box flex={1} minWidth={0}>
            {viewProductsButton("100%")}
          </Box>
          {!isApiSync && <Box flexShrink={0}>{downloadButton()}</Box>}
        </Flex>
      </Flex>
    );
  }

  return (
    <Grid
      templateColumns={templateColumns}
      gap={"16px"}
      alignItems={"center"}
      padding={"16px"}
      borderBottom={hasBorderBottom ? "0.5px solid #D4D4D1" : undefined}
    >
      {kindCell}

      {timeText}

      <Flex flexDirection={"column"} gap={"2px"}>
        {countLines}
      </Flex>

      <Flex flexDirection={"column"} gap={"8px"} alignItems={"flex-start"}>
        {statusBadge}
        {viewProductsButton()}
      </Flex>

      <Flex justifyContent={"flex-end"}>{downloadButton("100%")}</Flex>
    </Grid>
  );
}

export default PathPickSyncDownloadHistoryListItem;
